// Shows what one export run contains: workbooks and their sheets with row
// counts, the document mirror figures, and the Drive copy result.
import React, { useEffect, useState } from 'react';
import { X, FileSpreadsheet, FolderOpen, Cloud, ExternalLink, AlertTriangle } from 'lucide-react';
import { tenantExportService } from '@/services/tenantExportService';
import type { TenantExportManifest, TenantExportRun } from '@/types/tenant-export.types';
import { formatBytes } from './TenantExportRunsTable';

interface TenantExportManifestModalProps {
  run: TenantExportRun;
  onClose: () => void;
}

export const TenantExportManifestModal: React.FC<TenantExportManifestModalProps> = ({ run, onClose }) => {
  const [manifest, setManifest] = useState<TenantExportManifest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    tenantExportService
      .getManifest(run.id)
      .then((m) => { if (!cancelled) setManifest(m); })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Chargement impossible';
        setError(/not found|404|introuvable/i.test(message)
          ? "Les fichiers de cet export ne sont plus dans le stockage (export antérieur à la mise en place du stockage, ou purgé). Lancez un nouvel export."
          : message);
      });
    return () => { cancelled = true; };
  }, [run.id]);

  const sheetsByWorkbook = new Map<string, TenantExportManifest['sheets']>();
  for (const sheet of manifest?.sheets ?? []) {
    if (!sheetsByWorkbook.has(sheet.workbook)) sheetsByWorkbook.set(sheet.workbook, []);
    sheetsByWorkbook.get(sheet.workbook)!.push(sheet);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between rounded-t-xl bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-4 text-white">
          <div>
            <h2 className="text-lg font-semibold">{run.tenantName} — export du {manifest?.date ?? '…'}</h2>
            <p className="text-sm text-white/70">Classeurs Excel, feuilles et documents inclus</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-white/80 hover:bg-white/10 hover:text-white"><X size={20} /></button>
        </div>

        <div className="overflow-y-auto p-6 space-y-6">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!manifest && !error && <p className="text-sm text-gray-500">Chargement…</p>}

          {manifest && (
            <>
              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800"><FileSpreadsheet size={16} /> Classeurs Excel ({manifest.workbooks.length})</h3>
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {manifest.workbooks.map((wb) => (
                    <div key={wb.key} className="px-4 py-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-900">{wb.name}.xlsx</span>
                        <span className="text-gray-500">{formatBytes(wb.bytes)}</span>
                      </div>
                      <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
                        {(sheetsByWorkbook.get(wb.name) ?? []).map((s) => (
                          <li key={s.sheet}>{s.sheet} <span className="text-gray-400">({s.rows.toLocaleString('fr-FR')})</span></li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800"><FolderOpen size={16} /> Documents</h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Recensés" value={manifest.documents.listed} />
                  <Stat label="Copiés ce passage" value={manifest.documents.copied} />
                  <Stat label="Déjà à jour" value={manifest.documents.unchanged} />
                  <Stat label="Introuvables" value={manifest.documents.missing} warn={manifest.documents.missing > 0} />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Un dossier par thème (Personnes, Finance, Paie, Demandes, Cours, Structure, Institution), puis par année académique, puis par personne ; fichiers nommés <code>&lt;CIN&gt;_&lt;TYPE&gt;.&lt;ext&gt;</code>. Le miroir est incrémental : seuls les nouveaux fichiers sont copiés.
                </p>
              </section>

              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800"><Cloud size={16} /> Copie Google Drive</h3>
                {!manifest.drive && <p className="text-sm text-gray-500">Non copié sur Drive (Drive non configuré au moment de l'export).</p>}
                {manifest.drive?.error && (
                  <p className="flex items-start gap-2 text-sm text-red-600"><AlertTriangle size={16} className="mt-0.5 shrink-0" /> {manifest.drive.error}</p>
                )}
                {manifest.drive && !manifest.drive.error && (
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
                    <span>{manifest.drive.workbooks} classeurs</span>
                    <span>documents +{manifest.drive.documentsCopied} (={manifest.drive.documentsUnchanged})</span>
                    <span className="text-gray-500">{new Date(manifest.drive.syncedAt).toLocaleString('fr-FR')}</span>
                    {manifest.drive.folderLink && (
                      <a href={manifest.drive.folderLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                        Ouvrir dans Drive <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                )}
              </section>

              {manifest.tablesNotExported.length > 0 && (
                <details className="text-xs text-gray-500">
                  <summary className="cursor-pointer">Tables techniques non exportées ({manifest.tablesNotExported.length})</summary>
                  <p className="mt-1 break-words">{manifest.tablesNotExported.join(', ')}</p>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; warn?: boolean }> = ({ label, value, warn }) => (
  <div className={`rounded-lg border p-3 ${warn ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-lg font-semibold ${warn ? 'text-amber-700' : 'text-gray-900'}`}>{value.toLocaleString('fr-FR')}</p>
  </div>
);
